var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { ConsentStatus, IdentifiedPatientState, primaryConsentTypes, } from "healthex-common/consent.dtos";
import { isPatientConsentEnrolledActionNeeded, isPatientConsentWalletEnrolled } from "healthex-common/patient.sharing.utils";
import { ProjectType, StudyPatientConsentStatusSearch, StudyStatus } from "healthex-common/study.dtos";
import { computed, observable } from "mobx";
import { Disposer } from "../Infra/Disposer";
import { getObservableT } from "../Infra/i18next";
import { NOT_LOADED } from "../Infra/RemoteData";
import { getPatientDateString, getPatientStatusChip } from "../patient.utils";
import { buildGetCustomizedText } from "./RequestDetailsView";
import { getProjectDisplayTitle, getStudySectionImages, getTranslatedStudyText } from "./TranslationService";
const t = getObservableT("patients");
export class PatientResearchStudyDetailsViewModelProps {
    constructor(container, modalStudyId) {
        this.container = container;
        this.modalStudyId = modalStudyId;
    }
}
export class PatientResearchStudyDetailsViewModel {
    constructor(props) {
        var _a;
        this.modalStudyId = null;
        this.disposer = new Disposer();
        this.studyRevokeConsentDialogOpen = false;
        this.studyRevokeConsentStudyId = null;
        this.studyRevokeConsentSucceeded = false;
        this.byStudyConsentRequest = NOT_LOADED;
        this.patientStudyConsent = NOT_LOADED;
        this.isRevokeConsentButtonLoading = false;
        this.activityTimeline = NOT_LOADED;
        this.whatYouAgreedToOpen = false;
        this.studyManager = props.container.studyManager;
        this.patientResearchDashboardUIManager = props.container.patientResearchDashboardUIManager;
        this.patientConsentUIManager = props.container.patientConsentUIManager;
        this.routingUIManager = props.container.routingUIManager;
        this.patientService = props.container.patientService;
        this.authManager = props.container.authManager;
        this.browserLanguageUIManager = props.container.browserLanguageUIManager;
        this.modalStudyId = (_a = props.modalStudyId) !== null && _a !== void 0 ? _a : null;
    }
    run() {
        this.disposer.reaction(() => this.studyId, async () => {
            await this.loadConsentRequest();
        }, { fireImmediately: true });
        this.disposer.reaction(() => this.activeConsent, async () => {
            await this.loadPatientStudyConsent();
        }, { fireImmediately: true });
        this.disposer.reaction(() => this.studyId, async () => {
            await this.loadActivityTimeline();
        }, { fireImmediately: true });
    }
    stop() {
        this.disposer.dispose();
    }
    async loadConsentRequest() {
        var _a, _b, _c;
        if (this.studyId === null)
            return;
        if (this.authManager.id === null)
            return;
        const { results } = await this.patientService.postSearchForPatientStudyConsents(this.authManager.id, {
            consentType: primaryConsentTypes,
            includeConsentingByLink: true,
            patientState: (_a = this.authManager.state) !== null && _a !== void 0 ? _a : undefined,
            distinctPending: true,
            includePatientStudyConsentStatuses: [ConsentStatus.OPTED_IN, ConsentStatus.OPTED_OUT],
            studyId: (_b = this.studyId) !== null && _b !== void 0 ? _b : undefined,
            study: {
                excludeStatuses: [StudyStatus.COMPLETED, StudyStatus.WITHDRAWN],
                excludeDeId: true,
                patientConsentStatusSearch: StudyPatientConsentStatusSearch.INCLUDE_ALL_OPTED_IN,
                patientId: (_c = this.authManager.id) !== null && _c !== void 0 ? _c : undefined,
            },
        }, 0);
        if (results.length === 0) {
            this.byStudyConsentRequest = null;
            return;
        }
        this.byStudyConsentRequest = {
            study: results[0].study,
            consents: results.flatMap((result) => result.consents),
        };
    }
    async loadPatientStudyConsent() {
        if (this.studyId === null)
            return;
        if (this.authManager.id === null)
            return;
        if (this.byStudyConsentRequest === NOT_LOADED || this.byStudyConsentRequest === null)
            return;
        if (this.activeConsent === null)
            return;
        this.patientStudyConsent = await this.patientService.getPatientStudyConsent(this.authManager.id, this.studyId, true);
    }
    async loadActivityTimeline() {
        if (this.studyId === null)
            return;
        if (this.authManager.id === null)
            return;
        this.activityTimeline = await this.patientService.getActivityTimeline(this.authManager.id, this.studyId);
    }
    get studyId() {
        var _a, _b, _c;
        return (_c = (_a = this.modalStudyId) !== null && _a !== void 0 ? _a : (_b = this.routingUIManager.currentRouteParams) === null || _b === void 0 ? void 0 : _b.studyId) !== null && _c !== void 0 ? _c : null;
    }
    get isConsentRequestLoaded() {
        return this.byStudyConsentRequest !== NOT_LOADED;
    }
    get allConsents() {
        if (this.byStudyConsentRequest === NOT_LOADED || this.byStudyConsentRequest === null)
            return [];
        return this.byStudyConsentRequest.consents;
    }
    get activeConsent() {
        var _a;
        return (_a = this.allConsents.find((consent) => isPatientConsentWalletEnrolled(consent.state) && !consent.pending)) !== null && _a !== void 0 ? _a : null;
    }
    get study() {
        var _a, _b;
        if (this.byStudyConsentRequest === NOT_LOADED)
            return null;
        return (_b = (_a = this.byStudyConsentRequest) === null || _a === void 0 ? void 0 : _a.study) !== null && _b !== void 0 ? _b : null;
    }
    get patientFriendlyTitle() {
        if (!this.study)
            return null;
        return getProjectDisplayTitle(this.study, this.browserLanguageUIManager.currentLanguage) || null;
    }
    get organizationName() {
        if (!this.study)
            return null;
        return this.study.addedByOrganization.name;
    }
    get reasonForRequest() {
        if (!this.study)
            return null;
        const translatedRequestReason = getTranslatedStudyText(this.study, "purposeOfDataBriefEducation", this.browserLanguageUIManager.currentLanguage);
        return translatedRequestReason || null;
    }
    get patientFriendlySummary() {
        var _a, _b, _c, _d;
        if (!this.study)
            return null;
        const translatedSummary = getTranslatedStudyText(this.study, "patientFriendlySummary", this.browserLanguageUIManager.currentLanguage);
        return (_d = (_b = translatedSummary !== null && translatedSummary !== void 0 ? translatedSummary : (_a = this.study) === null || _a === void 0 ? void 0 : _a.patientFriendlySummary) !== null && _b !== void 0 ? _b : (_c = this.study) === null || _c === void 0 ? void 0 : _c.briefSummary) !== null && _d !== void 0 ? _d : null;
    }
    get description() {
        return this.isAIProject ? this.reasonForRequest : this.patientFriendlySummary || this.reasonForRequest;
    }
    get sponsorOrganizationName() {
        var _a, _b;
        return (_b = (_a = this.study) === null || _a === void 0 ? void 0 : _a.sponsorOrganizationName) !== null && _b !== void 0 ? _b : null;
    }
    showWhatYouAgreedTo() {
        this.whatYouAgreedToOpen = true;
    }
    hideWhatYouAgreedTo() {
        this.whatYouAgreedToOpen = false;
    }
    get whatYouAgreedToContent() {
        var _a, _b, _c;
        const language = this.browserLanguageUIManager.currentLanguage;
        return {
            isIAS: false,
            isAIFlow: this.isAIProject,
            isScreenshotMode: false,
            patientFriendlyTitle: (_a = this.patientFriendlyTitle) !== null && _a !== void 0 ? _a : "",
            aiToolName: (_c = (_b = this.study) === null || _b === void 0 ? void 0 : _b.aiToolName) !== null && _c !== void 0 ? _c : "",
            study: this.study,
            getCustomizedText: buildGetCustomizedText(this.study, language),
            getSectionImages: (sectionName, additionalSectionId) => getStudySectionImages(this.study, language, sectionName, additionalSectionId),
        };
    }
    get isAIProject() {
        if (!this.study)
            return false;
        return this.study.projectType === ProjectType.AI_DATA_USE;
    }
    get bannerInfo() {
        var _a, _b;
        if (this.study === null)
            return null;
        return {
            orgLogo: (_a = this.study.organizationIconUrl) !== null && _a !== void 0 ? _a : null,
            orgLogoRectangular: (_b = this.study.organizationRectangularIconUrl) !== null && _b !== void 0 ? _b : null,
        };
    }
    consentBucket(consent) {
        if (!!consent.pending ||
            consent.state === IdentifiedPatientState.ENROLLING ||
            consent.state === IdentifiedPatientState.ENROLLING_INTERESTED ||
            consent.state === IdentifiedPatientState.ENROLLING_CONTACTED ||
            (isPatientConsentWalletEnrolled(consent.state) && isPatientConsentEnrolledActionNeeded(consent))) {
            return "actionNeeded";
        }
        if (isPatientConsentWalletEnrolled(consent.state))
            return "active";
        if (consent.state === IdentifiedPatientState.DECLINED_ENROLLMENT || consent.state === IdentifiedPatientState.REVOKED_ENROLLMENT) {
            return "inactive";
        }
        return null;
    }
    buildConsentSection(consent, bucket) {
        const isInactiveReReviewable = consent.state === IdentifiedPatientState.DECLINED_ENROLLMENT || consent.state === IdentifiedPatientState.REVOKED_ENROLLMENT;
        const isPreConsentActionNeeded = bucket === "actionNeeded" && !isPatientConsentWalletEnrolled(consent.state);
        return {
            key: consent.id,
            chip: getPatientStatusChip(consent),
            title: this.patientFriendlyTitle,
            date: getPatientDateString(consent.timestamp),
            description: this.description,
            showUpdateRecords: !this.isAIProject &&
                isPatientConsentWalletEnrolled(consent.state) &&
                (!!consent.updateRecordsNeeded || !!consent.recordsRetrievalNeeded),
            showActivityTimeline: !isPreConsentActionNeeded,
            showShareRecordsUI: isPreConsentActionNeeded || isInactiveReReviewable,
            showDownloadUI: isPatientConsentWalletEnrolled(consent.state),
        };
    }
    get currentSection() {
        if (this.byStudyConsentRequest === NOT_LOADED || this.study === null)
            return null;
        const byBucket = { actionNeeded: [], active: [], inactive: [] };
        for (const consent of this.allConsents) {
            const bucket = this.consentBucket(consent);
            if (bucket !== null)
                byBucket[bucket].push(consent);
        }
        for (const bucket of ["actionNeeded", "active", "inactive"]) {
            const consents = byBucket[bucket];
            if (consents.length === 0)
                continue;
            const newestConsent = consents.reduce((newest, consent) => new Date(consent.timestamp) > new Date(newest.timestamp) ? consent : newest);
            return this.buildConsentSection(newestConsent, bucket);
        }
        return null;
    }
    showStudyRevokeConsentDialog() {
        this.studyRevokeConsentDialogOpen = true;
        this.studyRevokeConsentStudyId = this.studyId;
        this.studyRevokeConsentSucceeded = false;
    }
    hideStudyRevokeConsentDialog() {
        this.studyRevokeConsentDialogOpen = false;
        this.studyRevokeConsentStudyId = null;
        this.studyRevokeConsentSucceeded = false;
    }
    async markStudyRevokeConsentSucceeded() {
        this.studyRevokeConsentSucceeded = true;
        await Promise.all([this.loadConsentRequest(), this.loadActivityTimeline()]);
    }
    startConsentFlow() {
        if (this.studyId === null)
            return;
        this.patientConsentUIManager.startConsentFlow(this.studyId, null, null);
    }
    dataUpdateConsentFlow() {
        if (!this.studyId)
            return;
        this.patientConsentUIManager.dataUpdateConsentFlow(this.studyId);
    }
}
__decorate([
    observable
], PatientResearchStudyDetailsViewModel.prototype, "studyRevokeConsentDialogOpen", void 0);
__decorate([
    observable
], PatientResearchStudyDetailsViewModel.prototype, "studyRevokeConsentStudyId", void 0);
__decorate([
    observable
], PatientResearchStudyDetailsViewModel.prototype, "studyRevokeConsentSucceeded", void 0);
__decorate([
    observable
], PatientResearchStudyDetailsViewModel.prototype, "byStudyConsentRequest", void 0);
__decorate([
    observable
], PatientResearchStudyDetailsViewModel.prototype, "patientStudyConsent", void 0);
__decorate([
    observable
], PatientResearchStudyDetailsViewModel.prototype, "isRevokeConsentButtonLoading", void 0);
__decorate([
    observable
], PatientResearchStudyDetailsViewModel.prototype, "activityTimeline", void 0);
__decorate([
    computed
], PatientResearchStudyDetailsViewModel.prototype, "studyId", null);
__decorate([
    computed
], PatientResearchStudyDetailsViewModel.prototype, "isConsentRequestLoaded", null);
__decorate([
    computed
], PatientResearchStudyDetailsViewModel.prototype, "allConsents", null);
__decorate([
    computed
], PatientResearchStudyDetailsViewModel.prototype, "activeConsent", null);
__decorate([
    computed
], PatientResearchStudyDetailsViewModel.prototype, "study", null);
__decorate([
    computed
], PatientResearchStudyDetailsViewModel.prototype, "patientFriendlyTitle", null);
__decorate([
    computed
], PatientResearchStudyDetailsViewModel.prototype, "organizationName", null);
__decorate([
    computed
], PatientResearchStudyDetailsViewModel.prototype, "reasonForRequest", null);
__decorate([
    computed
], PatientResearchStudyDetailsViewModel.prototype, "patientFriendlySummary", null);
__decorate([
    computed
], PatientResearchStudyDetailsViewModel.prototype, "description", null);
__decorate([
    computed
], PatientResearchStudyDetailsViewModel.prototype, "sponsorOrganizationName", null);
__decorate([
    observable
], PatientResearchStudyDetailsViewModel.prototype, "whatYouAgreedToOpen", void 0);
__decorate([
    computed
], PatientResearchStudyDetailsViewModel.prototype, "whatYouAgreedToContent", null);
__decorate([
    computed
], PatientResearchStudyDetailsViewModel.prototype, "isAIProject", null);
__decorate([
    computed
], PatientResearchStudyDetailsViewModel.prototype, "bannerInfo", null);
__decorate([
    computed
], PatientResearchStudyDetailsViewModel.prototype, "currentSection", null);
